import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { AssetTypeConfig } from './utils/assetTypeConfig';
import TextField from './fields/TextField';
import TextArea from './fields/TextArea';
import RichTextEditor from './fields/RichTextEditor';
import SelectField from './fields/SelectField';
import TagsInput from './fields/TagsInput';
import DateTimeField from './fields/DateTimeField';
import URLField from './fields/URLField';
import FileUploader from './fields/FileUploader';
import JSONEditor from './fields/JSONEditor';
import MultiSelectField from './fields/MultiSelectField';
import VariationManager from './fields/VariationManager';
import AdCampaignSection from './sections/AdCampaignSection';
import AudienceFormModal from '../../Dashboard/clients/AudienceFormModal';

interface AssetFormContainerProps {
  assetType: string;
  config: AssetTypeConfig;
  formData: Record<string, any>;
  errors: Record<string, string>;
  onChange: (fieldName: string, value: any) => void;
  clientId?: number;
}

export default function AssetFormContainer({
  assetType,
  config,
  formData,
  errors,
  onChange,
  clientId
}: AssetFormContainerProps) {
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

  const [clientAudiences, setClientAudiences] = useState<any[]>([]);
  const [selectedAudiences, setSelectedAudiences] = useState<any[]>([]);
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [newlyCreatedAudienceId, setNewlyCreatedAudienceId] = useState<number | null>(null);

  // Load audiences for ad campaigns
  useEffect(() => {
    const isAdCampaign = assetType.includes('_ads');
    if (isAdCampaign) {
      loadAudiences();
    }
  }, [assetType, clientId]);

  const loadAudiences = async () => {
    try {
      // Use provided clientId, fallback to 1 if not provided
      const effectiveClientId = clientId || 1;
      const response = await axios.get(`${API_BASE_URL}/audiences?client_id=${effectiveClientId}`);
      setClientAudiences(response.data.audiences || []);

      // Auto-select newly created audience if it exists
      if (newlyCreatedAudienceId) {
        const currentSelection = formData.saved_audience || [];
        const newSelection = [...currentSelection, newlyCreatedAudienceId.toString()];
        onChange('saved_audience', newSelection);
        handleAudiencesSelect(newSelection);
        setNewlyCreatedAudienceId(null);
      }
    } catch (error) {
      console.error('Failed to load audiences:', error);
      setClientAudiences([]);
    }
  };

  const handleAudienceCreated = async () => {
    // Reload audiences after creation
    await loadAudiences();
    setShowAudienceModal(false);
  };

  const handleAudiencesSelect = (audienceIds: string[]) => {
    if (!audienceIds || audienceIds.length === 0) {
      setSelectedAudiences([]);
      return;
    }

    // Find all selected audiences
    const audiences = clientAudiences.filter(a => audienceIds.includes(a.id.toString()));
    setSelectedAudiences(audiences);

    // Helper to parse JSON fields
    const parseJsonField = (field: string | null): string[] => {
      if (!field) return [];
      try {
        return JSON.parse(field);
      } catch {
        return [];
      }
    };

    // Combine targeting from all selected audiences
    const combinedTargeting: Record<string, any> = {};

    // Combine arrays (union of all values)
    const arrayFields = [
      'locations', 'languages', 'job_titles', 'job_functions', 'seniorities',
      'industries', 'company_sizes', 'interests', 'behaviors',
      'custom_audiences', 'lookalike_audiences'
    ];

    arrayFields.forEach(field => {
      const allValues = new Set<string>();
      audiences.forEach(audience => {
        const values = parseJsonField((audience as any)[field]);
        values.forEach(v => allValues.add(v));
      });
      if (allValues.size > 0) {
        combinedTargeting[field] = Array.from(allValues);
      }
    });

    // For age range, use the widest range (lowest min, highest max)
    const ageMin = Math.min(...audiences.map(a => a.age_min).filter(Boolean));
    const ageMax = Math.max(...audiences.map(a => a.age_max).filter(Boolean));
    if (ageMin !== Infinity) combinedTargeting.age_min = ageMin;
    if (ageMax !== -Infinity) combinedTargeting.age_max = ageMax;

    // For gender, if any audience has 'all', use 'all', otherwise combine
    const genders = audiences.map(a => a.gender).filter(Boolean);
    if (genders.includes('all') || genders.length === 0) {
      combinedTargeting.gender = 'all';
    } else {
      combinedTargeting.gender = genders[0]; // Use first non-all gender
    }

    // Apply combined targeting to form
    Object.entries(combinedTargeting).forEach(([key, value]) => {
      onChange(key, value);
    });

    // For Google Ads, map locations to keywords if no keywords exist
    if (assetType === 'google_ads' && combinedTargeting.locations && !formData.keywords?.length) {
      onChange('keywords', combinedTargeting.locations);
    }
  };

  const renderField = (field: typeof config.fields[0]) => {
    // Check conditional display
    if (field.conditional) {
      const conditionValue = formData[field.conditional.field];
      if (conditionValue !== field.conditional.value) {
        return null;
      }
    }

    const commonProps = {
      label: field.label,
      value: field.type === 'tags'
        ? (formData[field.name] ?? field.defaultValue ?? [])
        : (formData[field.name] ?? field.defaultValue ?? ''),
      onChange: (value: any) => onChange(field.name, value),
      error: errors[field.name],
      required: field.required,
      placeholder: field.placeholder,
      helpText: field.helpText
    };

    switch (field.type) {
      case 'text':
        return (
          <TextField
            {...commonProps}
            maxLength={field.validation?.maxLength}
          />
        );

      case 'email':
        return (
          <TextField
            {...commonProps}
            type="email"
          />
        );

      case 'number':
        return (
          <TextField
            {...commonProps}
            type="number"
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      case 'textarea':
        return (
          <TextArea
            {...commonProps}
            maxLength={field.validation?.maxLength}
            rows={4}
          />
        );

      case 'richtext':
        return (
          <RichTextEditor
            {...commonProps}
          />
        );

      case 'select':
        return (
          <SelectField
            {...commonProps}
            options={field.options || []}
          />
        );

      case 'tags':
        // Check if this is a variation field (for ads, email, or social)
        const isVariationField =
          field.name.includes('_variations') ||
          field.name.includes('_variation') ||
          field.name === 'headlines' ||
          field.name === 'descriptions' ||
          field.name === 'subject_variations' ||
          field.name === 'preheader_variations' ||
          field.name === 'post_text_variations';

        if (isVariationField) {
          // Determine max variations and character limits based on field name
          let maxVars = 10; // default
          let charLimit = field.validation?.maxLength;

          // Ad-specific limits
          if (field.name === 'headlines') maxVars = 15;
          else if (field.name === 'descriptions') maxVars = 4;
          else if (field.name.includes('primary_text')) maxVars = 5;
          else if (field.name.includes('headline')) maxVars = 5;
          else if (field.name.includes('description')) maxVars = 5;
          else if (field.name.includes('intro_text')) maxVars = 5;

          // Email-specific limits
          else if (field.name === 'subject_variations') {
            maxVars = 5;
            charLimit = 100;
          }
          else if (field.name === 'preheader_variations') {
            maxVars = 3;
            charLimit = 150;
          }

          // Social-specific limits (platform-dependent)
          else if (field.name === 'post_text_variations') {
            maxVars = 5;
            // Character limit based on platform
            const platform = formData.platform;
            if (platform === 'twitter') charLimit = 280;
            else if (platform === 'facebook') charLimit = 63206;
            else if (platform === 'linkedin') charLimit = 3000;
            else if (platform === 'instagram') charLimit = 2200;
            else charLimit = 2200; // default to Instagram limit
          }

          return (
            <VariationManager
              {...commonProps}
              maxLength={charLimit}
              maxVariations={maxVars}
              minVariations={field.required ? 1 : 0}
            />
          );
        }

        return (
          <TagsInput
            {...commonProps}
            maxLength={field.validation?.maxLength}
          />
        );

      case 'multiselect':
        // Special handling for audience multi-selector
        if (field.name === 'saved_audience') {
          const audienceOptions = clientAudiences.map(aud => ({
            value: aud.id.toString(),
            label: `${aud.name} (${aud.platform})`
          }));

          return (
            <div key={field.name}>
              <div className="flex gap-2">
                <div className="flex-1">
                  <MultiSelectField
                    {...commonProps}
                    options={audienceOptions}
                    onChange={(value: any) => {
                      onChange(field.name, value);
                      handleAudiencesSelect(value);
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowAudienceModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                  title="Create New Audience"
                >
                  <Plus className="w-4 h-4" />
                  New
                </button>
              </div>
              {selectedAudiences.length > 0 && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="text-green-700">✓</span>
                      <span className="ml-2 text-green-900">
                        <strong>{selectedAudiences.length}</strong> audience{selectedAudiences.length !== 1 ? 's' : ''} selected - targeting combined
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(field.name, []);
                        handleAudiencesSelect([]);
                      }}
                      className="text-red-600 hover:text-red-700 text-xs font-medium"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedAudiences.map(aud => (
                      <span
                        key={aud.id}
                        className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                      >
                        {aud.name} ({aud.platform})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        }

        // Regular multiselect field
        return (
          <MultiSelectField
            {...commonProps}
            options={field.options || []}
          />
        );

      case 'date':
        return (
          <DateTimeField
            {...commonProps}
            type="date"
          />
        );

      case 'time':
        return (
          <DateTimeField
            {...commonProps}
            type="time"
          />
        );

      case 'url':
        return (
          <URLField
            {...commonProps}
          />
        );

      case 'file':
        return (
          <FileUploader
            {...commonProps}
            accept={config.mimeType}
          />
        );

      case 'json':
        return (
          <JSONEditor
            {...commonProps}
          />
        );

      default:
        return null;
    }
  };

  // Check asset type for special rendering
  const isAdCampaign = assetType.includes('_ads');
  const isEmailAsset = assetType === 'email';
  const isSocialAsset = assetType === 'social';

  // Group fields by section for ad campaigns
  const groupAdFields = () => {
    const basicFields = ['campaign_name', 'campaign_type', 'ad_format', 'objective', 'platform', 'platforms', 'placement'];
    const copyFields = ['headlines', 'descriptions', 'primary_text', 'headline', 'description', 'intro_text',
      'primary_text_variations', 'headline_variations', 'description_variations', 'intro_text_variations',
      'ad_images', 'ad_videos', 'banner_creatives', 'video_creative', 'companion_banner', 'audio_file', 'companion_image',
      'call_to_action', 'display_url', 'final_url', 'link_url', 'path1', 'path2', 'voiceover_script'];
    const targetingFields = ['saved_audience', 'keywords', 'negative_keywords', 'locations', 'languages', 'devices', 'audiences',
      'age_min', 'age_max', 'gender', 'interests', 'behaviors', 'custom_audiences', 'lookalikes',
      'job_titles', 'job_functions', 'seniorities', 'industries', 'company_sizes', 'companies', 'skills', 'groups',
      'ad_sizes', 'dsp', 'inventory_types', 'targeting_strategy', 'retargeting_lists', 'contextual_categories',
      'excluded_categories', 'excluded_domains', 'genres', 'dayparts', 'networks', 'shows', 'playlists', 'podcasts',
      'markets', 'household_income', 'presence_of_children'];
    const budgetFields = ['budget', 'budget_type', 'bid_strategy', 'start_date', 'end_date', 'status'];
    const advancedFields = ['pixel_id', 'carousel_cards', 'ad_extensions_call', 'ad_extensions_sitelinks',
      'lead_gen_form_id', 'conversation_starter', 'viewability', 'frequency_cap', 'frequency_period',
      'video_resolution', 'aspect_ratio', 'video_duration', 'video_format', 'audio_duration', 'audio_format',
      'audio_bitrate', 'music_bed', 'ad_pod_position', 'companion_banner_size', 'companion_display_url',
      'interactive_elements', 'dynamic_insertion'];

    return {
      basic: config.fields.filter(f => basicFields.includes(f.name)),
      copy: config.fields.filter(f => copyFields.includes(f.name)),
      targeting: config.fields.filter(f => targetingFields.includes(f.name)),
      budget: config.fields.filter(f => budgetFields.includes(f.name)),
      advanced: config.fields.filter(f => advancedFields.includes(f.name))
    };
  };

  // Helper to get grid column class based on width
  const getWidthClass = (width?: 'full' | 'half' | 'third') => {
    switch (width) {
      case 'half':
        return 'col-span-6';
      case 'third':
        return 'col-span-4';
      default:
        return 'col-span-12';
    }
  };

  // Render email asset with sections
  const renderEmailForm = () => {
    const variationFields = config.fields.filter(f => f.name.includes('_variations'));
    const senderFields = config.fields.filter(f => ['from_name', 'from_email', 'reply_to', 'send_date'].includes(f.name));
    const contentFields = config.fields.filter(f => ['html_content', 'plain_text', 'images'].includes(f.name));
    const trackingFields = config.fields.filter(f => f.name.startsWith('utm_'));

    return (
      <div className="space-y-4">
        {/* A/B Testing Variations */}
        <AdCampaignSection title="A/B Testing Variations" icon="settings" defaultExpanded={true}>
          <div className="space-y-4">
            {variationFields.map(field => (
              <div key={field.name}>
                {renderField(field)}
              </div>
            ))}
          </div>
        </AdCampaignSection>

        {/* Sender Information */}
        <AdCampaignSection title="Sender Information" icon="settings" defaultExpanded={true}>
          <div className="grid grid-cols-12 gap-6">
            {senderFields.map(field => (
              <div key={field.name} className={getWidthClass(field.width)}>
                {renderField(field)}
              </div>
            ))}
          </div>
        </AdCampaignSection>

        {/* Email Content */}
        <AdCampaignSection title="Email Content & Images" icon="settings" defaultExpanded={true}>
          <div className="space-y-4">
            {contentFields.map(field => (
              <div key={field.name}>
                {renderField(field)}
              </div>
            ))}
          </div>
        </AdCampaignSection>

        {/* Tracking */}
        <AdCampaignSection title="UTM Tracking Parameters" icon="settings" defaultExpanded={false} badge="Optional">
          <div className="grid grid-cols-12 gap-6">
            {trackingFields.map(field => (
              <div key={field.name} className={getWidthClass(field.width)}>
                {renderField(field)}
              </div>
            ))}
          </div>
        </AdCampaignSection>
      </div>
    );
  };

  // Render social media asset with sections
  const renderSocialForm = () => {
    const platformFields = config.fields.filter(f => f.name === 'platform');
    const variationFields = config.fields.filter(f => f.name.includes('_variations'));
    const hashtagFields = config.fields.filter(f => f.name.includes('hashtag_set'));
    const settingsFields = config.fields.filter(f =>
      ['mentions', 'link', 'cta', 'images', 'schedule_date', 'schedule_time'].includes(f.name)
    );

    return (
      <div className="space-y-4">
        {/* Platform Selection */}
        <AdCampaignSection title="Platform Selection" icon="settings" defaultExpanded={true}>
          <div className="space-y-4">
            {platformFields.map(field => (
              <div key={field.name}>
                {renderField(field)}
              </div>
            ))}
          </div>
        </AdCampaignSection>

        {/* Post Copy Variations */}
        <AdCampaignSection title="Post Copy Variations" icon="settings" defaultExpanded={true}>
          <div className="space-y-4">
            {variationFields.map(field => (
              <div key={field.name}>
                {renderField(field)}
              </div>
            ))}
          </div>
        </AdCampaignSection>

        {/* Hashtag Sets */}
        <AdCampaignSection title="Hashtag Testing Sets" icon="settings" defaultExpanded={true}>
          <div className="space-y-4">
            {hashtagFields.map(field => (
              <div key={field.name}>
                {renderField(field)}
              </div>
            ))}
          </div>
        </AdCampaignSection>

        {/* Post Settings */}
        <AdCampaignSection title="Post Settings & Scheduling" icon="settings" defaultExpanded={true}>
          <div className="grid grid-cols-12 gap-6">
            {settingsFields.map(field => (
              <div key={field.name} className={getWidthClass(field.width)}>
                {renderField(field)}
              </div>
            ))}
          </div>
        </AdCampaignSection>
      </div>
    );
  };

  const renderAdCampaignForm = () => {
    const sections = groupAdFields();

    return (
      <div className="space-y-4">
        {/* Basic Settings */}
        {sections.basic.length > 0 && (
          <AdCampaignSection title="Campaign Settings" icon="settings" defaultExpanded={true}>
            <div className="grid grid-cols-12 gap-6">
              {sections.basic.map(field => (
                <div key={field.name} className={getWidthClass(field.width)}>
                  {renderField(field)}
                </div>
              ))}
            </div>
          </AdCampaignSection>
        )}

        {/* Ad Copy & Creative */}
        {sections.copy.length > 0 && (
          <AdCampaignSection title="Ad Copy & Creative" icon="settings" defaultExpanded={true}>
            <div className="grid grid-cols-12 gap-6">
              {sections.copy.map(field => (
                <div key={field.name} className={getWidthClass(field.width)}>
                  {renderField(field)}
                </div>
              ))}
            </div>
          </AdCampaignSection>
        )}

        {/* Targeting */}
        {sections.targeting.length > 0 && (
          <AdCampaignSection
            title="Targeting & Audiences"
            icon="targeting"
            defaultExpanded={false}
            badge={`${sections.targeting.filter(f => formData[f.name]).length} configured`}
          >
            <div className="grid grid-cols-12 gap-6">
              {sections.targeting.map(field => (
                <div key={field.name} className={getWidthClass(field.width)}>
                  {renderField(field)}
                </div>
              ))}
            </div>
          </AdCampaignSection>
        )}

        {/* Budget & Schedule */}
        {sections.budget.length > 0 && (
          <AdCampaignSection title="Budget & Schedule" icon="budget" defaultExpanded={true}>
            <div className="grid grid-cols-12 gap-6">
              {sections.budget.map(field => (
                <div key={field.name} className={getWidthClass(field.width)}>
                  {renderField(field)}
                </div>
              ))}
            </div>
          </AdCampaignSection>
        )}

        {/* Advanced Settings */}
        {sections.advanced.length > 0 && (
          <AdCampaignSection
            title="Advanced Settings"
            icon="settings"
            defaultExpanded={false}
            badge="Optional"
          >
            <div className="grid grid-cols-12 gap-6">
              {sections.advanced.map(field => (
                <div key={field.name} className={getWidthClass(field.width)}>
                  {renderField(field)}
                </div>
              ))}
            </div>
          </AdCampaignSection>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {config.label} Details
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          {config.requiresUpload
            ? 'Upload your file and provide additional information.'
            : isAdCampaign
            ? 'Configure your advertising campaign. Fields are organized into collapsible sections for easier navigation.'
            : 'Fill in the details below. All content will be saved directly to the system.'}
        </p>
      </div>

      {isAdCampaign ? (
        renderAdCampaignForm()
      ) : isEmailAsset ? (
        renderEmailForm()
      ) : isSocialAsset ? (
        renderSocialForm()
      ) : (
        <div className="grid gap-6">
          {config.fields.map(field => (
            <div key={field.name}>
              {renderField(field)}
            </div>
          ))}
        </div>
      )}

      {/* Character counts for specific asset types */}
      {assetType === 'social' && formData.post_text && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">
            Character count: <strong>{formData.post_text.length}</strong>
            {formData.platform === 'twitter' && ' / 280'}
            {formData.platform === 'facebook' && ' / 63,206'}
            {formData.platform === 'linkedin' && ' / 3,000'}
            {formData.platform === 'instagram' && ' / 2,200'}
          </p>
        </div>
      )}

      {assetType === 'meta' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-700">
            <strong>SEO Tips:</strong> Keep title under 60 characters and description under 160 characters for optimal display in search results.
          </p>
        </div>
      )}

      {assetType === 'ads' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-purple-700">
            <strong>Google Ads Guidelines:</strong> Headlines max 30 characters, descriptions max 90 characters. Use multiple variations for better performance.
          </p>
        </div>
      )}

      {/* Inline Audience Creation Modal */}
      {showAudienceModal && (
        <AudienceFormModal
          isOpen={showAudienceModal}
          onClose={() => setShowAudienceModal(false)}
          clientId={clientId || 1}
          audience={null}
          onSuccess={handleAudienceCreated}
        />
      )}
    </div>
  );
}